import Box from '@mui/material/Box';
import { bool, func, number, object, string } from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';
import ContentLoader from 'react-content-loader';
import { List } from 'react-window';
import { InfiniteLoader } from 'react-window-infinite-loader';
import { I18n } from 'utils/i18n';
import MemberListItem from './MemberListItem';
import SectionHeader from './SectionHeader';

const ROW_HEIGHT = 80;

/**
 * Builds an index mapper for the virtualized list that interleaves
 * section headers with member rows based on backend-provided group counts.
 *
 * Groups order: full (watched+seen / read+seen), partial (seen only), none (no engagement).
 * Empty groups are skipped (no header rendered).
 */
const buildIndexMapper = (groupCounts, contentMode) => {
  if (!groupCounts) return null;

  const groups = [];
  const sectionTitles = {
    video: {
      full: I18n.t('EmployeeListDrawer.watchedAndSeen'),
      partial: I18n.t('EmployeeListDrawer.seenOnly'),
      none: I18n.t('EmployeeListDrawer.noEngagement'),
    },
    article: {
      full: I18n.t('EmployeeListDrawer.readAndSeen'),
      partial: I18n.t('EmployeeListDrawer.seenOnly'),
      none: I18n.t('EmployeeListDrawer.noEngagement'),
    },
  };

  const titles = sectionTitles[contentMode] || sectionTitles.video;
  let memberOffset = 0;

  ['full', 'partial', 'none'].forEach(groupKey => {
    const count = groupCounts[groupKey] || 0;
    if (count > 0) {
      groups.push({
        key: groupKey,
        title: titles[groupKey],
        memberStart: memberOffset,
        memberEnd: memberOffset + count - 1,
        count,
      });
      memberOffset += count;
    }
  });

  // Assign virtual indices
  const items = []; // { type: 'header' | 'member', groupKey, memberIndex, title }
  groups.forEach(group => {
    items.push({ type: 'header', title: group.title, groupKey: group.key });
    for (let i = 0; i < group.count; i += 1) {
      items.push({
        type: 'member',
        memberIndex: group.memberStart + i,
        groupKey: group.key,
      });
    }
  });

  return {
    totalVirtualCount: items.length,
    getItem: index => items[index] || null,
  };
};

const MemberRow = ({
  ariaAttributes,
  index,
  style,
  membersRef,
  membersRevision = 0,
  showReadStatus,
  contentMode = null,
  indexMapper = null,
}) => {
  const rowStyle = {
    ...style,
    '--members-revision': membersRevision,
  };
  if (indexMapper) {
    const item = indexMapper.getItem(index);
    if (!item) return null;

    if (item.type === 'header') {
      return <SectionHeader {...ariaAttributes} title={item.title} style={rowStyle} />;
    }

    const member = membersRef.current[item.memberIndex];
    if (!member) {
      const skeletonH = ROW_HEIGHT;
      return (
        <Box {...ariaAttributes} sx={{ flexGrow: 1 }} style={rowStyle}>
          <ContentLoader width={400} height={skeletonH} viewBox={`0 0 400 ${skeletonH}`}>
            <circle cx="22" cy="32" r="22px" />
            <rect x="60" y="10" ry="3" width="100" height="10" />
            <rect x="60" y="30" rx="3" ry="3" width="200" height="10" />
          </ContentLoader>
        </Box>
      );
    }

    return (
      <MemberListItem
        {...ariaAttributes}
        member={member}
        showReadStatus={showReadStatus}
        contentMode={contentMode}
        engagementGroup={item.groupKey}
        style={rowStyle}
      />
    );
  }

  const member = membersRef.current[index];
  return member ? (
    <MemberListItem
      {...ariaAttributes}
      member={member}
      showReadStatus={showReadStatus}
      contentMode={contentMode}
      style={rowStyle}
    />
  ) : (
    <Box {...ariaAttributes} sx={{ flexGrow: 1 }} style={rowStyle}>
      <ContentLoader width={400} height={80} viewBox="0 0 400 80">
        {!showReadStatus || <circle cx="9" cy="31" r="9px" />}
        <circle cx={showReadStatus ? 60 : 22} cy="32" r="22px" />
        <rect x={showReadStatus ? 99 : 60} y="30" rx="3" ry="3" width="200" height="10" />
        <rect x={showReadStatus ? 99 : 60} y="10" ry="3" width="100" height="10" />
      </ContentLoader>
    </Box>
  );
};

MemberRow.propTypes = {
  ariaAttributes: object.isRequired,
  index: number.isRequired,
  style: object.isRequired,
  membersRef: object.isRequired,
  membersRevision: number,
  showReadStatus: bool.isRequired,
  contentMode: string,
  indexMapper: object,
};

const MemberList = ({
  totalCount = 1000,
  membersRef,
  membersRevision = 0,
  loadMoreItems,
  showReadStatus,
  contentMode = null,
  groupCounts = null,
}) => {
  const hasGrouping = contentMode && contentMode !== 'basic' && groupCounts;

  const indexMapper = useMemo(() => {
    if (!hasGrouping) return null;
    return buildIndexMapper(groupCounts, contentMode);
  }, [hasGrouping, groupCounts, contentMode]);

  const virtualCount = indexMapper ? indexMapper.totalVirtualCount : totalCount;

  const rowProps = useMemo(
    () => ({
      membersRef,
      membersRevision,
      showReadStatus,
      contentMode,
      indexMapper,
    }),
    [membersRef, membersRevision, showReadStatus, contentMode, indexMapper],
  );

  const isRowLoaded = useCallback(
    index => {
      if (indexMapper) {
        const item = indexMapper.getItem(index);
        if (!item) return true;
        if (item.type === 'header') return true;
        return !!membersRef.current[item.memberIndex];
      }
      return !!membersRef.current[index];
    },
    [membersRef, indexMapper],
  );

  const loadMoreRows = useCallback(
    async (startIndex, stopIndex) => {
      if (!indexMapper) {
        await loadMoreItems(startIndex, stopIndex);
        return;
      }

      let minMember = Infinity;
      let maxMember = -1;
      for (let i = startIndex; i <= stopIndex; i += 1) {
        const item = indexMapper.getItem(i);
        if (item && item.type === 'member') {
          minMember = Math.min(minMember, item.memberIndex);
          maxMember = Math.max(maxMember, item.memberIndex);
        }
      }
      if (minMember <= maxMember) {
        await loadMoreItems(minMember, maxMember);
      }
    },
    [indexMapper, loadMoreItems],
  );

  return (
    <Box
      sx={{
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <InfiniteLoader
        minimumBatchSize={100}
        isRowLoaded={isRowLoaded}
        rowCount={virtualCount}
        loadMoreRows={loadMoreRows}
      >
        {({ onRowsRendered }) => (
          <List
            onRowsRendered={(visible, all) => onRowsRendered(all)}
            rowComponent={MemberRow}
            rowCount={virtualCount}
            rowHeight={ROW_HEIGHT}
            rowProps={rowProps}
            style={{ flex: '1 1 auto', height: '100%', width: '100%', minHeight: 0 }}
          />
        )}
      </InfiniteLoader>
    </Box>
  );
};

MemberList.propTypes = {
  membersRef: object.isRequired,
  membersRevision: number,
  showReadStatus: bool.isRequired,
  loadMoreItems: func.isRequired,
  totalCount: number,
  contentMode: string,
  groupCounts: object,
};

export default memo(MemberList);
